import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'WeDo HR Services';
const DEFAULT_DESCRIPTION = 'WeDo HR Services - Your strategic partner in human resources. Premium recruitment, professional training, HR consulting, and career advisory services across Europe, Middle East, and GCC regions.';
const DEFAULT_DESCRIPTION_AR = 'ويدو لخدمات الموارد البشرية - شريككم الاستراتيجي في الموارد البشرية. خدمات التوظيف والتدريب المهني والاستشارات والإرشاد المهني في أوروبا والشرق الأوسط ودول الخليج.';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://www.wedohr.net';

/**
 * SEO Component for managing page meta tags
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.titleAr - Arabic page title
 * @param {string} props.description - Meta description
 * @param {string} props.descriptionAr - Arabic meta description
 * @param {string} props.keywords - Meta keywords
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {string} props.ogType - Open Graph type (website, article, etc.)
 * @param {string} props.ogImage - Open Graph image URL
 * @param {Object} props.structuredData - JSON-LD structured data
 * @param {string} props.language - Current language (en/ar)
 * @param {boolean} props.noIndex - Whether to add noindex
 */
export const SEO = ({
  title,
  titleAr,
  description,
  descriptionAr,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  structuredData,
  additionalSchemas = [],
  language = 'en',
  noIndex = false
}) => {
  // Determine which content to use based on language
  const pageTitle = language === 'ar' && titleAr ? titleAr : title;
  const pageDescription = language === 'ar' && descriptionAr ? descriptionAr : description;
  
  const fullTitle = pageTitle ? `${pageTitle} | ${SITE_NAME}` : SITE_NAME;
  const metaDescription = pageDescription || (language === 'ar' ? DEFAULT_DESCRIPTION_AR : DEFAULT_DESCRIPTION);
  
  // Use production domain for canonical URLs
  const PRODUCTION_DOMAIN = 'https://www.wedohr.net';
  const canonical = canonicalUrl ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${PRODUCTION_DOMAIN}${canonicalUrl}`) : null;
  
  // Ensure image URL is absolute for social sharing
  const absoluteImageUrl = ogImage ? (
    ogImage.startsWith('http') ? ogImage : 
    ogImage.startsWith('/api/') ? `${PRODUCTION_DOMAIN}${ogImage}` :
    ogImage.startsWith('/') ? `${PRODUCTION_DOMAIN}${ogImage}` : ogImage
  ) : null;

  // Combine all structured data schemas
  const allSchemas = [
    ...(structuredData ? [structuredData] : []),
    ...additionalSchemas
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonical && <meta property="og:url" content={canonical} />}
      {absoluteImageUrl && <meta property="og:image" content={absoluteImageUrl} />}
      {absoluteImageUrl && <meta property="og:image:width" content="1200" />}
      {absoluteImageUrl && <meta property="og:image:height" content="630" />}
      <meta property="og:locale" content={language === 'ar' ? 'ar_SA' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'ar' ? 'en_US' : 'ar_SA'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {absoluteImageUrl && <meta name="twitter:image" content={absoluteImageUrl} />}
      
      {/* Structured Data / JSON-LD */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

/**
 * Generate Organization Schema
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'WeDo HR Services UG',
  alternateName: 'WeDo HR',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  description: DEFAULT_DESCRIPTION,
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'DE',
    addressLocality: 'Germany'
  },
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Place', name: 'GCC Countries' },
    { '@type': 'Continent', name: 'Africa' }
  ],
  knowsLanguage: ['en', 'ar'],
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['English', 'Arabic']
  }
});

/**
 * Generate JobPosting Schema
 * @param {Object} job - Job data
 * @param {string} language - Current language
 */
export const getJobPostingSchema = (job, language = 'en') => {
  const title = language === 'ar' && job.title_ar ? job.title_ar : job.title;
  const description = language === 'ar' && job.description_ar ? job.description_ar : job.description;
  const location = language === 'ar' && job.location_ar ? job.location_ar : job.location;
  const country = language === 'ar' && job.country_ar ? job.country_ar : job.country;

  const employmentTypeMap = {
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'contract': 'CONTRACTOR',
    'internship': 'INTERN',
    'temporary': 'TEMPORARY'
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: title,
    description: description,
    identifier: {
      '@type': 'PropertyValue',
      name: 'WeDo HR Services',
      value: job.job_id
    },
    datePosted: job.created_at,
    validThrough: new Date(new Date(job.created_at).setMonth(new Date(job.created_at).getMonth() + 3)).toISOString(),
    employmentType: employmentTypeMap[job.employment_type] || 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: job.company || 'WeDo HR Services',
      sameAs: BASE_URL
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: location,
        addressCountry: country,
        addressRegion: job.region
      }
    },
    ...(job.salary_min && job.salary_max && {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: job.currency || 'USD',
        value: {
          '@type': 'QuantitativeValue',
          minValue: job.salary_min,
          maxValue: job.salary_max,
          unitText: 'MONTH'
        }
      }
    }),
    ...(job.experience_years && {
      experienceRequirements: `${job.experience_years} years`
    }),
    skills: job.skills || [],
    jobLocationType: job.work_model === 'remote' ? 'TELECOMMUTE' : undefined
  };
};

/**
 * Generate Course Schema
 * @param {Object} course - Course data
 * @param {string} language - Current language
 */
export const getCourseSchema = (course, language = 'en') => {
  const name = language === 'ar' && course.title_ar ? course.title_ar : course.title;
  const description = language === 'ar' && course.description_ar ? course.description_ar : course.description;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: name,
    description: description,
    provider: {
      '@type': 'Organization',
      name: 'WeDo HR Services',
      sameAs: BASE_URL
    },
    ...(course.duration_days && {
      timeRequired: `P${course.duration_days}D`
    }),
    courseMode: course.delivery_mode === 'online' ? 'online' : 
                course.delivery_mode === 'hybrid' ? 'blended' : 'onsite',
    inLanguage: course.language === 'arabic' ? 'ar' : 
                course.language === 'bilingual' ? ['en', 'ar'] : 'en',
    ...(course.category && {
      about: {
        '@type': 'Thing',
        name: course.category
      }
    }),
    ...(course.price && {
      offers: {
        '@type': 'Offer',
        price: course.price,
        priceCurrency: course.currency || 'EUR',
        availability: 'https://schema.org/InStock'
      }
    }),
    hasCourseInstance: course.sessions?.map(session => ({
      '@type': 'CourseInstance',
      courseMode: session.delivery_mode === 'online' ? 'online' : 'onsite',
      startDate: session.start_date,
      endDate: session.end_date,
      location: {
        '@type': 'Place',
        name: session.city
      }
    })) || []
  };
};

/**
 * Generate BreadcrumbList Schema
 * @param {Array} items - Breadcrumb items [{name, url}]
 * Note: Per Google's requirements, all items except the last must have 'item' (URL).
 * The last item (current page) should omit 'item' entirely.
 */
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => {
    const isLastItem = index === items.length - 1;
    const listItem = {
      '@type': 'ListItem',
      position: index + 1,
      name: item.name
    };
    // Only include 'item' field if URL is provided (required for all except last item)
    // For the last item (current page), omit 'item' entirely per Google's spec
    if (item.url) {
      listItem.item = `${BASE_URL}${item.url}`;
    }
    return listItem;
  })
});

/**
 * Generate WebPage Schema
 * @param {Object} options
 */
export const getWebPageSchema = ({ name, description, url, type = 'WebPage' }) => ({
  '@context': 'https://schema.org',
  '@type': type,
  name: name,
  description: description,
  url: `${BASE_URL}${url}`,
  inLanguage: ['en', 'ar'],
  isPartOf: {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL
  }
});

export default SEO;
